import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { FiSearch, FiPrinter, FiPackage, FiUser, FiCalendar, FiClock, FiTruck, FiCheckCircle, FiAlertCircle, FiEye, FiX } from 'react-icons/fi';
import { MdOutlinePending, MdLocalShipping } from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import OrderPrint from './OrderPrint';

function OrdersList() {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const itemsPerPage = 10;
  const [printData, setPrintData] = useState(null);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [currentPrintOrder, setCurrentPrintOrder] = useState(null);

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  const handlePrintView = async (e, order) => {
    e.preventDefault();
    
    try {
      await axios.get('/sanctum/csrf-cookie');
      const res = await axios.post('/api/reinvoice/print', { id: order.id });
      
      const printDatas = {
        letter: res.data.cart,
        dates: res.data.dates,
        salestax: res.data.salestax,
        shipping: res.data.shipping,
        discount: res.data.discount,
        grandtotal: res.data.grandtotal,
        invoiceno: res.data.invoiceno,
        remainingbill: res.data.remainingbill,
        customer: res.data.customer,
        customeraddress: res.data.customeraddress,
        salesman: res.data.salesman,
        payment: res.data.payment,
        totalpay: res.data.totalpay,
        pbalance: res.data.pbalance,
        recovery: res.data.recovery,
        printingtime: res.data.printingtime,
        manualdate: res.data.manualdate,
        detail: res.data.detail,
        imagesrc: res.data.detail,
        entercash: res.data.entercash,
        isGift: 'No'
      };
      
      setPrintData(printDatas);
      setCurrentPrintOrder(order);
      setShowPdfViewer(true);
      
      // Scroll to PDF viewer
      setTimeout(() => {
        document.getElementById('pdf-viewer-section')?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
      
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to generate invoice');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    // Create a temporary div to generate PDF
    const printContent = document.getElementById('printable-invoice');
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice #${printData?.invoiceno}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
            @media print {
              body { margin: 0; padding: 0; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
    printWindow.close();
  };

  useEffect(() => {
    axios.get('api/onlinestore/orderslist')
      .then(res => {
        console.log(res.data);
        setOrders(res.data);
        setFilteredOrders(res.data);
      })
      .catch(err => {
        console.error('Error fetching orders:', err);
        toast.error('Failed to fetch orders');
      });
  }, []);

  useEffect(() => {
    let filtered = orders;
    
    if (searchTerm) {
      filtered = filtered.filter(order => 
        order.id.toString().includes(searchTerm) ||
        order.username?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => 
        statusFilter === 'pending' ? order.status === '2' : order.status !== '2'
      );
    }
    
    setFilteredOrders(filtered);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, orders]);

  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getStatusBadge = (status) => {
    if (status === '2') {
      return {
        label: 'Pending',
        icon: <MdOutlinePending size={14} />,
        color: 'bg-amber-100 text-amber-700 border-amber-200',
        bgColor: 'from-amber-50 to-amber-100'
      };
    } else {
      return {
        label: 'Shipped',
        icon: <MdLocalShipping size={14} />,
        color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        bgColor: 'from-emerald-50 to-emerald-100'
      };
    }
  };

  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === '2').length,
    shipped: orders.filter(o => o.status !== '2').length,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
              <FiPackage className="text-white text-2xl" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                Order Management
              </h1>
              <p className="text-slate-500 mt-1 text-sm">Track and manage all customer orders</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-md border border-slate-100 p-4 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Orders</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">{stats.total}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <FiPackage className="text-blue-600 text-xl" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md border border-slate-100 p-4 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Pending Orders</p>
                <p className="text-2xl font-bold text-amber-600 mt-1">{stats.pending}</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-xl">
                <FiAlertCircle className="text-amber-600 text-xl" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md border border-slate-100 p-4 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Shipped Orders</p>
                <p className="text-2xl font-bold text-emerald-600 mt-1">{stats.shipped}</p>
              </div>
              <div className="p-3 bg-emerald-100 rounded-xl">
                <FiCheckCircle className="text-emerald-600 text-xl" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-xl shadow-md border border-slate-100 p-4 mb-6">
          <div className="flex flex-col sm:flex-row justify-between gap-4">
            <div className="flex gap-3">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  statusFilter === 'all'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                All Orders
              </button>
              <button
                onClick={() => setStatusFilter('pending')}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  statusFilter === 'pending'
                    ? 'bg-amber-600 text-white shadow-md'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Pending
              </button>
              <button
                onClick={() => setStatusFilter('shipped')}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  statusFilter === 'shipped'
                    ? 'bg-emerald-600 text-white shadow-md'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Shipped
              </button>
            </div>
            
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search by Order ID or Customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full sm:w-80 transition-all duration-200"
              />
            </div>
          </div>
        </div>

        {/* PDF Viewer Section */}
        <AnimatePresence>
          {showPdfViewer && printData && (
            <motion.div
              id="pdf-viewer-section"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-8"
            >
              <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
                {/* Viewer Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      Invoice Preview - Order #{currentPrintOrder?.id}
                    </h2>
                    <p className="text-blue-100 text-sm mt-1">
                      Customer: {currentPrintOrder?.username}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handlePrint}
                      className="px-4 py-2 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors flex items-center gap-2"
                    >
                      <FiPrinter size={18} />
                      Print
                    </button>
                    <button
                      onClick={handleDownloadPDF}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center gap-2"
                    >
                      Download PDF
                    </button>
                    <button
                      onClick={() => {
                        setShowPdfViewer(false);
                        setPrintData(null);
                        setCurrentPrintOrder(null);
                      }}
                      className="p-2 text-white hover:bg-white/20 rounded-lg transition-colors"
                    >
                      <FiX size={24} />
                    </button>
                  </div>
                </div>
                
                {/* Scrollable Invoice Content */}
                <div className="p-6 bg-slate-50 max-h-[80vh] overflow-y-auto">
                  <div id="printable-invoice" className="bg-white shadow-lg rounded-lg">
                    <OrderPrint data={printData} />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Orders Table */}
        <div className="bg-white rounded-xl shadow-md border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-slate-50 to-white border-b-2 border-slate-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Order ID</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Customer</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Manual Date</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Created At</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Delivery Date</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <AnimatePresence>
                  {paginatedOrders.length > 0 ? (
                    paginatedOrders.map((order, index) => {
                      const status = getStatusBadge(order.status);
                      return (
                        <motion.tr
                          key={order.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="hover:bg-slate-50 transition-colors duration-200 group"
                        >
                          <td className="px-6 py-4">
                            <span className="text-sm font-semibold text-slate-800">#{order.id}</span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center">
                                <FiUser className="text-blue-600 text-sm" />
                              </div>
                              <span className="text-sm text-slate-700 font-medium">{order.username}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <FiCalendar className="text-slate-400 text-sm" />
                              <span className="text-sm text-slate-600">{order.manualdate || '—'}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border ${status.color}`}>
                              {status.icon}
                              {status.label}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <FiClock className="text-slate-400 text-sm" />
                              <span className="text-sm text-slate-600">
                                {new Date(order.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <FiTruck className="text-slate-400 text-sm" />
                              <span className="text-sm text-slate-600">{order.delivery_date || '—'}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2">
                              <button
                                onClick={(e) => handlePrintView(e, order)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 group-hover:scale-105"
                                title="View & Print Invoice"
                              >
                                <FiPrinter size={18} />
                              </button>
                              <button
                                onClick={() => handleViewDetails(order)}
                                className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all duration-200 group-hover:scale-105"
                                title="View Details"
                              >
                                <FiEye size={18} />
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="px-6 py-16 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div className="p-4 bg-slate-100 rounded-full">
                            <FiPackage size={32} className="text-slate-400" />
                          </div>
                          <p className="text-slate-500 font-medium">No orders found</p>
                          <p className="text-sm text-slate-400">Try adjusting your search or filters</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="text-sm text-slate-600">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredOrders.length)} of {filteredOrders.length} orders
                </div>
                
                <div className="flex gap-1.5">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    Previous
                  </button>
                  
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`w-9 h-9 text-sm font-semibold rounded-lg transition-all duration-200 ${
                          currentPage === pageNum
                            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md'
                            : 'text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Order Details Modal */}
      <AnimatePresence>
        {showDetailsModal && selectedOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowDetailsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl font-bold text-white">Order Details #{selectedOrder.id}</h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-white hover:bg-white/20 rounded-lg p-1 transition-colors"
                >
                  ✕
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500">Customer Name</p>
                    <p className="text-sm font-semibold text-slate-800">{selectedOrder.username}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500">Order Status</p>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border ${getStatusBadge(selectedOrder.status).color} mt-1`}>
                      {getStatusBadge(selectedOrder.status).icon}
                      {getStatusBadge(selectedOrder.status).label}
                    </span>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500">Created At</p>
                    <p className="text-sm font-semibold text-slate-800">{new Date(selectedOrder.created_at).toLocaleString()}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500">Manual Date</p>
                    <p className="text-sm font-semibold text-slate-800">{selectedOrder.manualdate || '—'}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500">Delivery Date</p>
                    <p className="text-sm font-semibold text-slate-800">{selectedOrder.delivery_date || '—'}</p>
                  </div>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={(e) => {
                      setShowDetailsModal(false);
                      handlePrintView(e, selectedOrder);
                    }}
                    className="flex-1 bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <FiPrinter size={18} />
                    View & Print Invoice
                  </button>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="flex-1 border-2 border-slate-200 text-slate-700 py-2 rounded-lg font-semibold hover:bg-slate-50 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default OrdersList;